import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
    Search,
    Star,
    Loader2,
    Sparkles,
    Eye,
    Layers,
} from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { Label } from "@/components/ui/label";
import { editorAPI } from "@/lib/editor/api";

interface Template {
    id: string;
    name: string;
    description?: string;
    category: string;
    features: string[];
    dataSources: string[];
    usageCount?: number;
    thumbnailUrl?: string;
    metadata: {
        version?: string;
        framework?: string;
        author?: string;
    };
    tags: string[];
}

export default function TemplatesFull() {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");
    const [cloning, setCloning] = useState<string | null>(null);
    const [showCloneModal, setShowCloneModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(
        null
    );
    const [projectName, setProjectName] = useState("");
    const [projectDescription, setProjectDescription] = useState("");
    const [projectMode] = useState<"power">("power"); // Always Power Mode
    const [generatingName, setGeneratingName] = useState(false);
    const navigate = useNavigate();
    const { canCreateProject } = usePermissions();

    // CDN URL for template assets
    const CDN_URL =
        import.meta.env.VITE_OES_STATIC_CDN ||
        "https://optidev-public-cdn-dev.nyc3.cdn.digitaloceanspaces.com/templates-assets/";

    useEffect(() => {
        loadTemplates();
    }, [selectedCategory, searchQuery]);

    const loadTemplates = async () => {
        try {
            setLoading(true);
            const filters: any = {};

            if (selectedCategory !== "all") {
                filters.category = selectedCategory;
            }
            if (searchQuery) {
                filters.search = searchQuery;
            }

            const data = await editorAPI.listTemplates(filters);
            setTemplates(data);
        } catch (error) {
            console.error("Failed to load templates:", error);
        } finally {
            setLoading(false);
        }
    };

  const handleOpenCloneModal = async (template: Template) => {
    setSelectedTemplate(template);
    setProjectName('');
    setProjectDescription('');
    setShowCloneModal(true);

    // Generate a suggested name automatically
    await generateProjectName();
  };

    const generateProjectName = async () => {
        try {
            setGeneratingName(true);
            const response = await editorAPI.suggestProjectName();
            setProjectName(response.suggestedName);
        } catch (error) {
            console.error("Failed to generate project name:", error);
            // Fallback to empty if generation fails
        } finally {
            setGeneratingName(false);
        }
    };

    const handleCloneTemplate = async () => {
        if (!selectedTemplate || !projectName.trim()) return;

        setCloning(selectedTemplate.id);
        setShowCloneModal(false);

        try {
            await editorAPI.cloneTemplate(selectedTemplate.id, {
                projectName,
                mode: projectMode,
            });

            // Navigate to projects list to see provisioning status
            navigate("/dashboard/editor");
        } catch (error: any) {
            console.error("Failed to clone template:", error);
        } finally {
            setCloning(null);
            setSelectedTemplate(null);
        }
    };

    const getFrameworkIcon = (framework?: string) => {
        switch (framework) {
            case "react":
                return "⚛️";
            case "vue":
                return "🟢";
            case "angular":
                return "🔺";
            case "vanilla":
                return "🟨";
            default:
                return "📦";
        }
    };

    const getThumbnailUrl = (thumbnailUrl?: string) => {
        if (!thumbnailUrl) return null;

        // If it's already a full URL, return as-is
        if (
            thumbnailUrl.startsWith("http://") ||
            thumbnailUrl.startsWith("https://")
        ) {
            return thumbnailUrl;
        }

        // Otherwise, prepend the CDN URL
        return `${CDN_URL}${thumbnailUrl}`;
    };

  return (
    <div className="min-h-full">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-14 lg:py-16">
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold mb-3">Start from a Template</h1>
          <p className="text-muted-foreground text-base sm:text-lg">
            Choose a pre-built dashboard template to get started quickly
          </p>
        </div>

            {/* Search and Filters */}
            <div className="mb-6 space-y-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                        placeholder="Search templates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10"
                    />
                </div>

                <div className="flex gap-2 flex-wrap">
                    {[
                        "all",
                        "starter",
                        "analytics",
                        "monitoring",
                        "retail",
                    ].map((category) => (
                        <Button
                            key={category}
                            variant={
                                selectedCategory === category
                                    ? "default"
                                    : "outline"
                            }
                            size="sm"
                            onClick={() => setSelectedCategory(category)}
                            className="capitalize">
                            {category === "all" ? "All Templates" : category}
                        </Button>
                    ))}
                </div>
            </div>

      {/* Template Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {/* Loading skeletons */}
          {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-border/50 overflow-hidden bg-card animate-pulse"
            >
              <div className="aspect-video bg-muted" />
              <div className="p-4 space-y-3">
                <div className="h-5 bg-muted rounded w-3/4" />
                <div className="flex gap-2">
                  <div className="h-6 bg-muted rounded-full w-20" />
                  <div className="h-6 bg-muted rounded-full w-16" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No templates found
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {templates.map((template) => (
            <div
              key={template.id}
              className="group relative rounded-2xl bg-card cursor-pointer overflow-hidden transition-all duration-300 border border-border/30 hover:border-primary/20 hover:shadow-[0_0_30px_rgba(168,85,247,0.4)] hover:-translate-y-1"
            >
              {/* Image */}
              <div className="aspect-video bg-muted/30 relative rounded-t-2xl overflow-hidden">
                {getThumbnailUrl(template.thumbnailUrl) ? (
                  <img
                    src={getThumbnailUrl(template.thumbnailUrl)!}
                    alt={template.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <span className="text-5xl block opacity-80">
                        {getFrameworkIcon(template.metadata.framework)}
                      </span>
                      <Layers className="w-10 h-10 text-muted-foreground/60 mx-auto mt-3" />
                    </div>
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <h3 className="font-semibold text-foreground line-clamp-1 flex-1 text-base">
                    {template.name}
                  </h3>
                  <Badge
                    variant="secondary"
                    className="capitalize bg-primary/10 text-primary border border-primary/20 flex-shrink-0 text-xs font-medium px-2 py-0.5"
                  >
                    {template.category}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {template.description || 'No description available'}
                </p>
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-white/80 dark:bg-black/75 flex items-center justify-center p-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="flex flex-col gap-3 w-full max-w-xs">
                  <Button
                    className="w-full shadow-lg bg-primary hover:bg-primary/90"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenCloneModal(template);
                    }}
                    disabled={cloning === template.id || !canCreateProject}
                    title={!canCreateProject ? "You don't have permission to create projects" : undefined}
                  >
                    {cloning === template.id && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Use Template
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full shadow-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.open(`/preview/template/${template.id}`, '_blank');
                    }}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Clone Template Modal */}
      <Modal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        title="Create Project from Template"
        size="md"
      >
        <div className="space-y-4">
          {selectedTemplate && (
            <div className="bg-muted p-3 rounded-lg mb-4">
              <p className="text-sm text-muted-foreground">Template:</p>
              <p className="font-medium text-foreground">{selectedTemplate.name}</p>
            </div>
          )}
          
          <div>
            <Label htmlFor="project-name">Project Name *</Label>
            <div className="flex gap-2 mt-1">
              <Input
                id="project-name"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="My Awesome Project"
                disabled={generatingName}
                className="flex-1"
                autoFocus
              />
              <Button
                type="button"
                variant="outline"
                onClick={generateProjectName}
                disabled={generatingName}
                className="h-10 px-3"
              >
                {generatingName ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
          
          <div>
            <Label htmlFor="project-description">Description (optional)</Label>
            <Input
              id="project-description"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              placeholder="Brief description of your project"
              className="mt-1"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowCloneModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCloneTemplate}
              disabled={!projectName.trim() || cloning !== null}
              className="flex-1"
            >
              {cloning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Project'
              )}
            </Button>
          </div>
        </div>
      </Modal>
      </div>
    </div>
  );
}
